// Map Unicode smart-quote/dash characters to their ASCII counterparts. The
// Apollo link layer (FormatSpecialCharactersMiddleware) already does this for
// plain-string variables, but it operates on the raw JSON STRING after
// stringify — so for log payloads that go over the wire as a JSON-encoded
// `data` variable, an unescaped smart quote inside a value gets rewritten to
// `"`, which is a structural JSON character and breaks the payload (Sentry
// AU-HORIZON-PROD-678). Normalising the values pre-stringify produces a JSON
// payload that contains only ASCII quotes from the start, leaving nothing
// for the middleware to corrupt.
const SMART_CHAR_MAP: { [key: string]: string } = {
  '‘': "'",
  '’': "'",
  '‛': "'",
  '“': '"',
  '”': '"',
  '‟': '"',
  '′': "'",
  '″': '"',
  '‵': "'",
  '‶': '"',
  '–': '-',
  '—': '-',
};
const SMART_CHAR_REGEXP = new RegExp(Object.keys(SMART_CHAR_MAP).join('|'), 'g');

// Recurse only into plain POJOs. Date / Map / Set / RegExp / class instances
// all satisfy `typeof === 'object'` but Object.entries returns `[]` for most
// of them — without this guard the previous code would silently rebuild those
// values as empty `{}` and discard the original data.
const isPlainObject = (value: unknown): value is { [key: string]: unknown } => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const normaliseSmartChars = (value: unknown): unknown => {
  if (typeof value === 'string') return value.replace(SMART_CHAR_REGEXP, (m): string => SMART_CHAR_MAP[m]);
  if (Array.isArray(value)) return value.map(normaliseSmartChars);
  if (isPlainObject(value)) {
    const out: { [key: string]: unknown } = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = normaliseSmartChars(v);
    }
    return out;
  }
  return value;
};

export const logPage = (startTime: number, eventData: any, logEvent: any, logTime = true): void => {
  const currentTime = new Date().getTime();
  const timeOnPage = (currentTime - startTime) / 1000;
  let pageData = { ...eventData };
  if (logTime)
    // To prevent unit test failures on mock functions due to arbitrary timeOnPage value
    pageData = { ...pageData, timeOnPage };
  logEvent({ variables: { data: JSON.stringify(normaliseSmartChars(pageData)) } });
};

export enum PATIENT_EVENTS {
  DUPLICATE_TRIGGERED = 'create_patient_duplicate_triggered',
  DUPLICATE_IGNORED = 'create_patient_duplicate_ignored',
  DUPLICATE_SEARCH_TIME = 'create_patient_duplicate_search_time',
  SUBMIT = 'create_patient_submit',
  START = 'create_patient_start',
}

export enum PRACTITIONER_LOG_EVENTS {
  // Exact record found
  PRACTITIONER_EXACT_MATCH = 'practitioner_exact_match',
  // No Exact record found
  PRACTITIONER_SELECTED = 'practitioner_selected',
  PRACTITIONER_NOT_FOUND = 'practitioner_not_found',
  PRACTITIONER_OVERRIDE_ENTRY = 'practitioner_override_entry',
  PRACTITIONER_SEARCHED = 'practitioner_searched',
  PRACTITIONER_RESULTS = 'practitioner_results',
}

export enum POPOVER_LOG_EVENTS {
  DISPLAYED = 'popover_displayed',
  NEXT_STEP = 'popover_next_step',
  COMPLETE = 'popover_complete',
  EXIT = 'popover_exit',
}

export enum BUZZ_WIDGET_POPOVER_LOG_EVENTS {
  CLICKED_LATEST_UPDATE = 'buzz_onboarding_widget_latest_update',
  CLICKED_GOT_IT = 'buzz_onboarding_widget_clicked_got_it',
}

export enum BUZZ_DASHBOARD_POPOVER_LOG_EVENTS {
  CLICKED_LATEST_UPDATE = 'buzz_onboarding_dashboard_latest_update',
  CLICKED_GOT_IT = 'buzz_onboarding_dashboard_clicked_got_it',
  CLICKED_BUZZ_SMS_TAB = 'buzz_onboarding_dashboard_tab',
}
